import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Cross, Shield, Scales, Heart, BookOpen, Users } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9d663873-ece1-460a-abc5-f98dcd211b2a/artifacts/qilm7q3e_A087EB80-08A6-4F5B-B90E-AA189E10D04B.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_chivalric-office/artifacts/ov9kxa3g_c1a05642-40fd-4887-83d5-0270eefaa86b.jpeg";
const CEREMONY_IMG_1 = "https://customer-assets.emergentagent.com/job_chivalric-office/artifacts/ov9kxa3g_c1a05642-40fd-4887-83d5-0270eefaa86b.jpeg";
const CEREMONY_IMG_2 = "https://customer-assets.emergentagent.com/job_chivalric-office/artifacts/2hebjf1p_e3acf92b-bcec-4e20-9c0b-79317d0fc1bf.jpeg";
const CEREMONY_IMG_3 = "https://customer-assets.emergentagent.com/job_chivalric-office/artifacts/jp18xs29_81cf8eff-c43b-4ea8-9957-f7f9d052a6db.jpeg";

const pillars = [
  { icon: Cross, title: 'Faith', description: 'Rooted in Christian principles and the traditions of chivalric orders.' },
  { icon: Shield, title: 'Heritage', description: 'Preserving the legacy of the Knights Templar in the Caribbean.' },
  { icon: Scales, title: 'Leadership', description: 'Cultivating principled leaders who serve with integrity.' },
  { icon: Heart, title: 'Service', description: 'Committed to charitable works and community support.' },
  { icon: BookOpen, title: 'Discipline', description: 'Upholding the code of honor and chivalric virtue.' },
  { icon: Users, title: 'Brotherhood', description: 'A fraternity bound by shared values and purpose.' },
];

const HomePage = () => {
  return (
    <PublicLayout>
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-[#1A1614]/60" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <img 
            src={LOGO_URL} 
            alt="Grand Priory Crest" 
            className="w-40 h-40 mx-auto mb-8 drop-shadow-2xl"
            data-testid="hero-crest"
          />
          <p className="text-overline text-[#C5A059] mb-4">SCMOTH 1804 OSMTJ</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white mb-6 tracking-tight">
            Grand Priory of<br />the Caribbean
          </h1>
          <p className="text-lg sm:text-xl text-[#F5F2EB]/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            A Christian chivalric and charitable Order dedicated to heritage, faith, leadership, 
            service, and discipline throughout the Caribbean region.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/membership"
              className="px-8 py-3 bg-[#7B1818] text-white font-medium hover:bg-[#5C1212] transition-colors"
              data-testid="cta-membership"
            >
              Inquire About Membership
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3 bg-transparent text-white border border-[#C5A059] font-medium hover:bg-[#C5A059]/10 transition-colors"
              data-testid="cta-contact"
            >
              Official Contact
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#C5A059] animate-bounce">
          <div className="w-6 h-10 border-2 border-[#C5A059] rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-[#C5A059] rounded-full" />
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-overline mb-4">Our Mission</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#1A1614] mb-8">
            Upholding the Traditions of Christian Chivalry
          </h2>
          <p className="text-lg text-[#4A453E] leading-relaxed">
            The Grand Priory of the Caribbean continues the noble traditions of the Sovereign 
            Military Order of the Temple of Jerusalem. We are committed to the defense of the 
            Christian faith, charitable works, and the preservation of chivalric virtues in 
            the modern world. Our Knights and Dames serve with honor, integrity, and dedication 
            to the betterment of our communities across the Caribbean.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 bg-[#F5F2EB]" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-overline mb-4">Our Foundations</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#1A1614]">
              Pillars of the Order
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <div 
                  key={pillar.title}
                  className="bg-white border border-[#D8D2C4] p-8 card-hover"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`pillar-${pillar.title.toLowerCase()}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#7B1818]/10 text-[#7B1818] mb-4">
                    <Icon size={24} weight="regular" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1A1614] mb-2">{pillar.title}</h3>
                  <p className="text-[#4A453E] text-sm leading-relaxed">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* History Brief */}
      <section className="py-20 bg-[#1A1614] text-[#F5F2EB]" data-testid="history-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-overline mb-4">Our Heritage</p>
              <h2 className="font-heading text-3xl sm:text-4xl mb-6">
                A Legacy Since 1118
              </h2>
              <p className="text-[#8A847A] leading-relaxed mb-6">
                The Grand Priory of the Caribbean traces its lineage through an unbroken succession 
                from the founding of the Knights Templar in 1118 Jerusalem. In that year, nine knights 
                led by Hugues de Payens pledged to protect Christian pilgrims traveling to the Holy Land. 
                The Order received formal papal recognition through the bull <em>Omne Datum Optimum</em> issued 
                by Pope Innocent II in 1139, which granted the Templars extraordinary privileges and 
                established them as one of the most powerful institutions in Christendom.
              </p>
              <p className="text-[#8A847A] leading-relaxed mb-6">
                Following the restoration of the Order under Napoleon Bonaparte in 1804, the Templar 
                tradition was brought back into public life after centuries. The Grand Priory of the 
                Caribbean was formally constituted in 2024 under the authority of the Grand Magisterium.
              </p>
              <p className="text-[#8A847A] leading-relaxed mb-8">
                Our jurisdiction encompasses the Caribbean region, where we continue to uphold the 
                principles of faith, charity, and chivalric honor that have defined our Order for 
                over nine centuries.
              </p>
              <Link
                to="/about"
                className="inline-block px-6 py-2 border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10 transition-colors"
                data-testid="learn-more-link"
              >
                Learn Our Full History
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 border border-[#C5A059]/30" />
              <img 
                src={CEREMONY_IMG_3}
                alt="Knights of the Order"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ceremony Gallery */}
      <section className="py-20 bg-[#F5F2EB]" data-testid="ceremony-gallery">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-overline mb-4">Our Ceremonies</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#1A1614]">
              Investitures & Gatherings
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="overflow-hidden">
              <img 
                src={CEREMONY_IMG_1}
                alt="Investiture Ceremony"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
              <p className="text-center text-sm text-[#4A453E] mt-3">Investiture Ceremony</p>
            </div>
            <div className="overflow-hidden">
              <img 
                src={CEREMONY_IMG_2}
                alt="Knighting Ceremony"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
              <p className="text-center text-sm text-[#4A453E] mt-3">Knighting Ceremony</p>
            </div>
            <div className="overflow-hidden">
              <img 
                src={CEREMONY_IMG_3}
                alt="Knights in Regalia"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
              <p className="text-center text-sm text-[#4A453E] mt-3">Knights in Regalia</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section 
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${CEREMONY_IMG_2})`
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-[#1A1614]/70" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <p className="text-overline mb-4">Join Our Brotherhood</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">
            Answer the Call to Chivalry
          </h2>
          <p className="text-[#F5F2EB]/80 mb-8 leading-relaxed">
            If you feel called to serve with honor and dedicate yourself to the principles 
            of Christian chivalry, we invite you to learn more about membership in the 
            Grand Priory of the Caribbean.
          </p>
          <Link
            to="/membership"
            className="inline-block px-8 py-3 bg-[#C5A059] text-[#1A1614] font-medium hover:bg-[#A68545] transition-colors"
            data-testid="cta-apply"
          >
            Begin Your Journey
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default HomePage;
